import { memo, ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, ErcProfile, PenaltyTier } from '@pmp/api';
import { useTableView } from '@pmp/hooks';
import { ListProps } from '@pmp/types';
import { useExportTemplatesStore } from '@pmp/stores';

import { ercColumns, ercProfilesFlatColumns, periodColumns } from '../tableConfig';
import { ErcProfileData } from '../interfaces';
import { Guard, useGuard } from '@ohpen/react-authz';
import { lockActionTitle } from '@pmp/utils';

const entityType = ActionEntityType.ERC_PROFILES;

export const ErcProfilesList = memo(({ isFetching, data, onCreate, onView, onEdit, onDelete }: ListProps<ErcProfile>): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
  const { tableViews, saveTableView, deleteTableView } = useTableView(entityType);
  const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });
  const { t } = useTranslation();

  const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.ERC_PROFILES), [getExportTemplates]);

  const getRowActions: (record: ErcProfile) => ReactElement[] = useCallback(
    record => {
      const rowActions = [
        <Tooltip key="view" title={t('global.buttons.view')} placement="top">
          <Button type="text" aria-label="View ERC" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={['ercProfiles:update']} flags={['enableErcProfilesCrud']}>
          <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Edit ERC"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
        </Guard>,
        <Guard key="delete" permissions={['ercProfiles:delete']} flags={['enableErcProfilesCrud']}>
          <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
            <Button
              type="text"
              aria-label="Delete ERC"
              icon={<DeleteOutlined key="delete" />}
              onClick={() => onDelete(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
        </Guard>,
      ];

      return rowActions;
    },
    [onDelete, onEdit, onView, t],
  );

  const tableProps: TableProps<ActionEntityType, ErcProfile, PenaltyTier, unknown, ErcProfileData> = useMemo(
    () => ({
      id: entityType,
      itemsLabel: t('pmp.erc.labels.ercProfile'),
      ref: tableRef,
      dataSource: data ?? [],
      columns: ercColumns,
      exportColumns: ercProfilesFlatColumns,
      rowKey: 'ercProfileId',
      pagination: true,
      scroll: { x: 'max-content', y: '100%' },
      loading: isFetching,
      getRowActions,
      expanded: {
        columns: periodColumns,
        columnsKey: 'penaltyTiers',
        rowKey: 'order',
      },
      showRowSelection,
      showViews: true,
      tableViews,
      saveTableView,
      deleteTableView,
    }),
    [t, data, isFetching, getRowActions, showRowSelection, tableViews, saveTableView, deleteTableView],
  );

  return (
    <Table {...tableProps}>
      <>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  editableFileName
                  fileName={`ERC profiles ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                  sheetName="ERC profiles"
                  disabled={isFetching}
                  loadingTemplates={isLoadingExportTemplates}
                  getTemplates={getTemplates}
                  modalDefaults={{ columnScope: 'template' }}
                  allowSearch={true}
                  exportView
                  flatten
                />
              </Guard>
              <Guard permissions={['ercProfiles:create']} flags={['enableErcProfilesCrud']}>
                <Table.CreateButton onClick={onCreate} />
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="pmp.erc.labels.xErcProfilesSelected">
          <Space size="middle">
            <Guard flags={['enableExports']}>
              <Table.ExportButton
                editableFileName
                fileName={`ERC profiles ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                sheetName="ERC profiles"
                disabled={isFetching}
                loadingTemplates={isLoadingExportTemplates}
                allowSearch={true}
                getTemplates={getTemplates}
                modalDefaults={{ columnScope: 'template' }}
                exportView
                flatten
              />
            </Guard>
          </Space>
        </Table.BulkBar>
      </>
    </Table>
  );
});
